'use client';

import React from 'react';

import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../primitives/table';
import { cn } from '../../utils/cn';
import { UzEmptyState } from '../feedback/UzEmptyState';
import { UzSkeleton } from '../feedback/UzSkeleton';

import type { ReactNode } from 'react';

export interface UzColumn<TRow> {
  /** Unique id for the column (used for keys + sort). */
  id: string;
  /** Header content. String or any node. */
  header: ReactNode;
  /** Cell renderer. Receives the row and the index. */
  cell: (row: TRow, index: number) => ReactNode;
  /** Width in any CSS unit, applied as inline style on the th. */
  width?: string | number;
  /** Tailwind classnames merged onto every <td> in this column. */
  className?: string;
  align?: 'left' | 'right' | 'center';
}

export interface UzTableProps<TRow> {
  columns: ReadonlyArray<UzColumn<TRow>>;
  data: ReadonlyArray<TRow>;
  /** Function returning a stable React key for each row. */
  getRowId: (row: TRow, index: number) => string;
  /** Show skeleton rows. */
  loading?: boolean;
  /** Custom empty state. Defaults to "No results". */
  emptyState?: ReactNode;
  caption?: ReactNode;
  /** Click handler for the entire row. */
  onRowClick?: (row: TRow) => void;
}

/**
 * Lightweight controlled table. For Excel-like grids (filter, group, virtualised),
 * use UzDataGrid (AG Grid wrapper, ships in W3).
 */
export const UzTable = <TRow,>({
  columns,
  data,
  getRowId,
  loading,
  emptyState,
  caption,
  onRowClick,
}: UzTableProps<TRow>): React.JSX.Element => {
  if (!loading && data.length === 0) {
    return (
      <>
        {emptyState ?? (
          <UzEmptyState title="No results" description="Try adjusting your filters." />
        )}
      </>
    );
  }

  return (
    <Table>
      {caption && <TableCaption>{caption}</TableCaption>}
      <TableHeader>
        <TableRow>
          {columns.map((col) => (
            <TableHead
              key={col.id}
              style={{ width: col.width }}
              className={cn(
                col.align === 'right' && 'text-right',
                col.align === 'center' && 'text-center',
              )}
            >
              {col.header}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading
          ? Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={`skeleton-${i}`}>
                {columns.map((col) => (
                  <TableCell key={col.id}>
                    <UzSkeleton variant="line" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          : data.map((row, i) => (
              <TableRow
                key={getRowId(row, i)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(onRowClick && 'cursor-pointer')}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    className={cn(
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                      col.className,
                    )}
                  >
                    {col.cell(row, i)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
      </TableBody>
    </Table>
  );
};
